import { ChangeDetectionStrategy, Component, Inject, ViewEncapsulation } from '@angular/core';
import { FormArray, FormControl, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { ConditionalRule, ConditionForm, CustomField, Operator, RuleCondition } from '@ee/common/models';

/** Data injected into the document conditional-rule dialog. */
export interface DocumentRuleDialogData {
  /** The existing rule for this document, if any. */
  rule: ConditionalRule | null;
  /** Fields available to build the conditions against. */
  sourceFields: CustomField[];
}

/**
 * Result emitted on close:
 * - `undefined`        → cancelled (no change)
 * - `{ rule: null }`   → remove the rule
 * - `{ rule: {...} }`  → save the rule
 */
export interface DocumentRuleDialogResult {
  rule: ConditionalRule | null;
}

/**
 * Modal for defining the optional conditional rule that gates whether a document is
 * auto-selected on step completion. Hosts one or more shared `ee-condition-builder`
 * rows ("When" conditions), combined with AND — matching the evaluators' implied-AND
 * semantics. Mirrors the intake "Rules" dialog pattern.
 */
@Component({
  selector: 'ee-document-rule-dialog',
  template: `
    <h2 mat-dialog-title>Document Rule</h2>
    <div mat-dialog-content class="document-rule-dialog">
      <p class="document-rule-dialog__intro">
        Only generate this document when the case meets <b>all</b> of the conditions below.
        Leave it unset to always generate the document.
      </p>

      @for (form of conditionForms.controls; track form; let i = $index) {
        @if (i > 0) {
          <div class="document-rule-dialog__and">AND</div>
        }
        <div class="document-rule-dialog__condition">
          <ee-condition-builder class="flex-1" [form]="form" [sourceFields]="data.sourceFields"></ee-condition-builder>
          @if (conditionForms.length > 1) {
            <button class="ee__button gray-bg" mat-icon-button type="button"
                    aria-label="Remove condition" tp="Remove condition" (click)="removeCondition(i)">
              <mat-icon fontSet="fa" fontIcon="fa-xmark"></mat-icon>
            </button>
          }
        </div>
      }

      <div class="document-rule-dialog__add">
        <button mat-button color="accent" type="button" (click)="addCondition()">ADD CONDITION</button>
      </div>

      @if (showError) {
        <div class="document-rule-dialog__error">
          Please complete the field, operator, and value for every condition.
        </div>
      }
    </div>
    <div mat-dialog-actions align="end">
      <button mat-button mat-dialog-close>CANCEL</button>
      @if (data.rule?.conditions?.length) {
        <button mat-button (click)="remove()">REMOVE RULE</button>
      }
      <button mat-flat-button color="accent" (click)="save()">SAVE</button>
    </div>
  `,
  styleUrls: ['./document-rule-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  standalone: false
})
export class DocumentRuleDialogComponent {
  /** One "When" condition form per AND condition, bound to the shared condition builders. */
  conditionForms: FormArray<FormGroup<ConditionForm>>;
  showError = false;

  constructor(
    private dialogRef: MatDialogRef<DocumentRuleDialogComponent, DocumentRuleDialogResult>,
    @Inject(MAT_DIALOG_DATA) public data: DocumentRuleDialogData
  ) {
    const conditions = this.data.rule?.conditions ?? [];
    const forms = conditions.length
      ? conditions.map((c) => this.buildConditionForm(c))
      : [this.buildConditionForm()];
    this.conditionForms = new FormArray<FormGroup<ConditionForm>>(forms);
  }

  /** Add another AND condition row. */
  addCondition(): void {
    this.conditionForms.push(this.buildConditionForm());
  }

  /** Remove the condition at the given index (kept ≥ 1; use REMOVE RULE to clear entirely). */
  removeCondition(index: number): void {
    if (this.conditionForms.length > 1) {
      this.conditionForms.removeAt(index);
    }
  }

  /** Validate every condition and save the rule, closing with the result. */
  save(): void {
    if (!this.conditionForms.controls.every((f) => this.isConditionValid(f))) {
      this.conditionForms.markAllAsTouched();
      this.showError = true;
      return;
    }

    const conditions: RuleCondition[] = this.conditionForms.controls.map((f) => {
      const { source_field, operator, value } = f.getRawValue();
      return { source_field: source_field, operator, value: operator === 'blank' ? null : value };
    });
    this.dialogRef.close({ rule: { conditions } });
  }

  /** Clear the rule from the document. */
  remove(): void {
    this.dialogRef.close({ rule: null });
  }

  private isConditionValid(form: FormGroup<ConditionForm>): boolean {
    const { source_field, operator, value } = form.getRawValue();
    if (!source_field || !operator) {
      return false;
    }
    if (operator === 'blank') {
      return true;
    }
    return !(value === null || value === undefined || value === '' || (Array.isArray(value) && value.length === 0));
  }

  private buildConditionForm(condition?: RuleCondition): FormGroup<ConditionForm> {
    return new FormGroup<ConditionForm>({
      source_field: new FormControl(condition?.source_field ?? null, Validators.required),
      operator: new FormControl<Operator>((condition?.operator ?? '=='), {
        nonNullable: true,
        validators: Validators.required
      }),
      value: new FormControl(condition?.value ?? null, Validators.required)
    });
  }
}
